import React, { useEffect, useRef, useState } from 'react';
import { css } from '@emotion/react';
import { token } from '@atlaskit/tokens';
import { getLocalStorageHistory, setLocalStorageItems } from '@utils/index';
import { SearchbarHistory } from '@components/learning/Searchbar/children/SearchbarHistory/SearchbarHistory';
import { DESKTOP_SCREEN_WIDTH, useDeviceSize } from '@hooks/useDeviceSize';
import SearchIcon from '@atlaskit/icon/glyph/search';
import { media } from '@atlaskit/primitives/responsive';
import { Button, ButtonAppearance, ButtonSize } from '@components/shared/Button';
import { Icon } from '@components/shared/Icon';
import Spinner from '@atlaskit/spinner';

/**
 * Searchbar component renders a search bar input using Atlassian Navigation.
 *
 * @param {string} value - The current value of the search input.
 * @param {function} onChange - Callback function to handle changes in the input value.
 * @returns {React.ReactElement} - The rendered Searchbar component.
 * */

export interface SearchbarProps {
  value: string;
  onChange: (value: string) => void;
  isLoading: boolean;
}

export const Searchbar = ({ value, onChange, isLoading }: SearchbarProps): React.ReactElement => {
  const [isHistoryVisible, setIsHistoryVisible] = useState(false);
  const [history, setHistory] = useState(getLocalStorageHistory());
  const searchRef = useRef<HTMLDivElement>(null);
  const deviceSize = useDeviceSize();
  const isRemoveIconVisible = !!value.length && (deviceSize >= DESKTOP_SCREEN_WIDTH || !isLoading);

  useEffect(() => {
    const searchElement = searchRef.current;
    if (!searchElement) {
      return;
    }

    const inputElement = searchElement.querySelector('input');
    if (!inputElement) {
      return;
    }

    const handleFocus = (): void => {
      setIsHistoryVisible(true);
    };

    const handleClickOutside = (event: MouseEvent): void => {
      if (searchRef.current && !searchRef.current.contains(event.target as Node)) {
        setIsHistoryVisible(false);
      }
    };

    inputElement.addEventListener('focus', handleFocus);
    document.addEventListener('mousedown', handleClickOutside);

    return () => {
      inputElement.removeEventListener('focus', handleFocus);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleRemoveItem = (itemToRemove: string): void => {
    const newHistory = history.filter((item) => item !== itemToRemove);
    setHistory(newHistory);
    setLocalStorageItems(newHistory);
  };

  const handleSelectItem = (selectedItem: string): void => {
    onChange(selectedItem);
    setIsHistoryVisible(false);
  };

  const onKeyDown = (event: React.KeyboardEvent<HTMLDivElement>): void => {
    if (event.key === 'Enter' && value.trim()) {
      if (!history.includes(value.trim()) && searchRef.current) {
        const newHistory = [...history, value.trim()];
        setLocalStorageItems(newHistory);

        const inputElement = searchRef.current.querySelector('input');

        if (inputElement) {
          inputElement.blur();
        }

        setIsHistoryVisible(false);
        setHistory(newHistory);
      }
    }
  };

  const filteredItems = history.filter((item) => item.toLowerCase().includes(value.toLowerCase()));

  const handleOnChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
    if (event.target instanceof HTMLInputElement) {
      onChange(event.target.value);
    }
  };

  const onClearSearchClick = (): void => {
    onChange('');
    setIsHistoryVisible(false);
  };

  return (
    <div css={searchStyles} onKeyDown={onKeyDown} tabIndex={0} ref={searchRef}>
      <span css={searchBeforeIconStyles}>
        <SearchIcon primaryColor={token('color.text.subtlest')} size="medium" label="search icon" />
      </span>
      {!!value.length && isLoading && (
        <span data-testid="search-loader" css={[searchAfterIconStyles, searchLoaderIconStyles]}>
          <Spinner size="small" />
        </span>
      )}
      {isRemoveIconVisible && (
        <span css={searchAfterIconStyles} onClick={onClearSearchClick}>
          <Button
            label=""
            size={ButtonSize.LARGE}
            iconBefore={<Icon type="cross" alt="cross icon" />}
            appearance={ButtonAppearance.ICON}
          />
        </span>
      )}
      <input
        placeholder={deviceSize < DESKTOP_SCREEN_WIDTH ? 'Search' : 'Search catalog'}
        value={value}
        onChange={handleOnChange}
      />
      {isHistoryVisible && history.length > 0 && (
        <SearchbarHistory items={filteredItems} onRemove={handleRemoveItem} onSelect={handleSelectItem} />
      )}
    </div>
  );
};

const searchStyles = css({
  position: 'relative',
  width: '100%',
  maxWidth: '240px',

  [media.below.md]: {
    maxWidth: '275px',
  },

  [media.below.xs]: {
    maxWidth: '100%',
  },

  input: {
    width: '100%',
    height: '32px',
    border: `1px solid ${token('color.border.input')}`,
    borderRadius: '5px !important',
    padding: `0 30px 0 ${token('space.500')}`,
    color: `${token('color.text')} !important`,
    fontSize: '14px',
    fontWeight: 400,
    lineHeight: '20px',
    outline: 'none',

    '&::placeholder': {
      color: token('color.text.subtlest'),
      fontFamily: 'SF Pro, sans-serif',
    },

    '&:focus': {
      border: `2px solid ${token('color.border.focused')} !important`,
    },

    [media.below.md]: {
      height: '40px',
      padding: `10px 30px 10px ${token('space.500')}`,
    },
  },
});

const searchBeforeIconStyles = css({
  position: 'absolute',
  left: '10px',
  top: '6px',

  "[role='img']": {
    width: '20px !important',
    height: '20px !important',
  },
});

const searchAfterIconStyles = css({
  position: 'absolute',
  right: token('space.100'),
  top: token('space.050'),
  cursor: 'pointer',

  "[role='img']": {
    width: '20px !important',
    height: '20px !important',
  },

  [media.below.md]: {
    top: token('space.100'),
  },
});

const searchLoaderIconStyles = css({
  cursor: 'default',
  right: '-24px',

  [media.below.md]: {
    top: token('space.050'),
    right: token('space.200'),
  },
});
